import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  Upload,
  Button,
  Typography,
  Table,
  Tag,
  message,
  Space,
  Modal,
  Row,
  Col,
  Alert,
  Select,
  Input,
  Form,
  DatePicker,
  Switch,
  Popconfirm,
} from "antd";
import {
  UploadOutlined,
  CalendarOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../../services/api";
import { all_routes } from "../../router/all_routes";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import CommonSelect from "../../../core/common/commonSelect";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Holiday {
  id: string | number;
  title: string;
  description?: string;
  date: string;
  start_date: string;
  end_date: string;
  type: string;
  is_public?: boolean;
  event_type?: number;
  company_id?: number;
  company_type?: string;
  created_by?: {
    user_id: number;
    name: string;
    uid: string;
  };
  status?: number;
  createdAt?: string;
  updatedAt?: string;
  is_generated?: boolean;
}

interface HolidayCSVRow {
  Date?: string;
  Title?: string;
  Description?: string;
  Type?: string;
  "Holiday Name"?: string;
}

interface Company {
  id: number;
  name: string;
  type: string;
}

const HolidayUpload: React.FC = () => {
  const signature = localStorage.getItem("signature");
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [selectedCompany, setSelectedCompany] = useState({
    companyId: "",
    companyName: "Select Company",
    companyType: "",
  });
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<"csv" | "excel" | "pdf">(
    "csv"
  );
  const [stats, setStats] = useState({
    total: 0,
    public: 0,
    private: 0,
    upcoming: 0,
  });
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const getSignature = (): string | null => localStorage.getItem("signature");

  const withSignature = <T extends object>(
    data?: T
  ): T & { signature: string | null } => ({
    ...(data || ({} as T)),
    signature: getSignature(),
  });

  useEffect(() => {
    fetchCompanyList();
  }, []);

  useEffect(() => {
    if (selectedCompany.companyId) {
      fetchHolidays(selectedCompany.companyId, selectedCompany.companyType);
    }
  }, [selectedCompany.companyId, selectedCompany.companyType]);

  const fetchCompanyList = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("userService/getAllCompanyList", {
        signature: getSignature(),
      });

      const companies = response.data?.data?.data || [];
      setCompanyList(companies);

      if (companies.length > 0) {
        const newCompany = {
          companyId: companies[0].id.toString(),
          companyName: companies[0].name,
          companyType: companies[0].type,
        };
        setSelectedCompany(newCompany);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to fetch companies");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolidays = async (companyId: string, companyType: string) => {
    setLoading(true);
    try {
      // Get public events with event_type = 2 (holidays)
      const response = await api.post(
        "activityCalendarService/getPublicHolidayByType",
        withSignature({
          event_type: 2, // Holiday type
          company_id: companyId,
          company_type: companyType,
        })
      );

      let holidaysData = response.data?.message[0]?.data || [];

      // Transform the data to match Holiday interface
      holidaysData = holidaysData.map((holiday: any) => ({
        ...holiday,
        date: holiday.start_date,
        type: holiday.is_public ? "Public" : "Private",
      }));

      setHolidays(holidaysData);

      const total = holidaysData.length;
      const publicHolidays = holidaysData.filter(
        (h: Holiday) => h.is_public
      ).length;
      const privateHolidays = total - publicHolidays;
      const upcoming = holidaysData.filter((h: Holiday) =>
        dayjs(h.start_date).isAfter(dayjs(), "day")
      ).length;

      setStats({
        total,
        public: publicHolidays,
        private: privateHolidays,
        upcoming,
      });
    } catch (error) {
      console.error("Error fetching holidays:", error);
      message.error("Failed to fetch holidays");
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (companyId: number, companyType: string) => {
    const selected = companyList.find(
      (c: any) => c.id === companyId && c.type === companyType
    );

    if (!selected) return;

    setSelectedCompany({
      companyId: String(selected.id),
      companyName: selected.name,
      companyType: selected.type,
    });
  };

  const handleBeforeUpload = (file: File) => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company before uploading");
      return false;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim(),
      complete: (result) => {
        const csvData = result.data as any[];

        const transformedData: Array<{
          title: string;
          description: string;
          date: string;
          type: string;
        }> = [];
        const errors: string[] = [];

        if (!csvData || csvData.length === 0) {
          message.error("CSV file is empty or could not be parsed");
          return;
        }

        for (let i = 0; i < csvData.length; i++) {
          const row = csvData[i];
          const rowNumber = i + 1;

          // Extract data with normalized header names
          const title =
            row.title || row["holiday name"] || row["holidayname"] || row.name;
          const date = row.date || row.date || row.startdate || row.start_date;
          const description = row.description || row.desc || row.notes || "";
          const type = row.type || row.category || "Public";

          if (!title || !date) {
            errors.push(`Row ${rowNumber}: Title and Date are required`);
            continue;
          }

          // Validate date format
          let formattedDate;
          try {
            const parsedDate = dayjs(date);
            if (!parsedDate.isValid()) {
              errors.push(
                `Row ${rowNumber}: Invalid date format. Got: "${date}"`
              );
              continue;
            }
            formattedDate = parsedDate.format("YYYY-MM-DD");
          } catch (error) {
            errors.push(
              `Row ${rowNumber}: Invalid date format. Got: "${date}"`
            );
            continue;
          }

          // Validate type
          const validType =
            type &&
              (type.toLowerCase() === "private" || type.toLowerCase() === "pr")
              ? "Private"
              : "Public";

          transformedData.push({
            title: title.toString().trim(),
            description: description.toString().trim(),
            date: formattedDate,
            type: validType,
          });
        }

        if (errors.length > 0) {
          message.error(
            `CSV validation errors: ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? `... and ${errors.length - 3} more` : ""
            }`
          );
          return;
        }

        if (transformedData.length === 0) {
          message.warning("No valid data found in the CSV file");
          return;
        }

        // Show confirmation modal
        Modal.confirm({
          title: "Confirm Holiday Upload",
          content: (
            <div>
              <p>
                Are you sure you want to upload {transformedData.length}{" "}
                holidays for {selectedCompany.companyName}?
              </p>
              {transformedData.length > 0 && (
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginTop: "10px",
                  }}
                >
                  <Table
                    size="small"
                    columns={[
                      {
                        title: "Title",
                        dataIndex: "title",
                        key: "title",
                        width: 150,
                      },
                      {
                        title: "Date",
                        dataIndex: "date",
                        key: "date",
                        width: 100,
                      },
                      {
                        title: "Type",
                        dataIndex: "type",
                        key: "type",
                        width: 80,
                      },
                    ]}
                    dataSource={transformedData.slice(0, 5)}
                    pagination={false}
                    scroll={{ x: 330 }}
                  />
                  {transformedData.length > 5 && (
                    <p
                      style={{
                        textAlign: "center",
                        marginTop: "5px",
                        fontSize: "12px",
                      }}
                    >
                      ... and {transformedData.length - 5} more
                    </p>
                  )}
                </div>
              )}
            </div>
          ),
          okText: "Upload",
          cancelText: "Cancel",
          onOk: () => handleBulkUpload(transformedData),
        });
      },
      error: (error) => {
        message.error("Failed to parse CSV file");
        console.error("CSV parsing error:", error);
      },
    });

    return false; // Prevent auto upload
  };

  const handleBulkUpload = async (holidayData: any[]) => {
    if (!selectedCompany.companyId) {
      message.warning("Please select a company");
      return;
    }

    setUploadLoading(true);

    try {
      const payload = {
        holidays: holidayData,
        company_id: selectedCompany.companyId, // string
        company_type: selectedCompany.companyType,
        signature: signature,
      };

      const response = await api.post(
        "activityCalendarService/bulkUploadHolidays",
        payload
      );

      const result = response.data?.message?.[0];
      const data = result?.data;

      if (result?.status) {
        const createdCount = Number(data?.created || 0);
        const duplicateCount = Array.isArray(data?.duplicates)
          ? data.duplicates.length
          : 0;

        if (createdCount > 0) {
          message.success(`Successfully uploaded ${createdCount} holiday(ies)`);
        }

        if (duplicateCount > 0) {
          message.info(`${duplicateCount} duplicate holiday(ies) were skipped`);
        }

        if (createdCount === 0 && duplicateCount === 0) {
          message.success(result.msg || "Holidays processed successfully");
        }

        fetchHolidays(selectedCompany.companyId, selectedCompany.companyType);
      } else {
        message.error(result?.msg || "Failed to upload holidays");
      }
    } catch (error: any) {
      console.error("Bulk upload error:", error);

      if (error.response?.data?.data?.errors) {
        const errors = error.response.data.data.errors;
        message.error(
          `Upload failed: ${errors.slice(0, 3).join(", ")}${errors.length > 3 ? "..." : ""
          }`
        );
      } else if (error.response?.data?.data?.msg) {
        message.error(error.response.data.data.msg);
      } else {
        message.error("Failed to upload holidays");
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCreateHoliday = async (values: any) => {
    try {
      const payload = {
        title: values.title,
        description: values.description,
        start_date: dayjs(values.date).format("YYYY-MM-DD"),
        end_date: dayjs(values.date).format("YYYY-MM-DD"),
        is_all_day: true,
        event_type: 2, // Holiday type
        is_public: values.type === "Public",
        signature: getSignature(),
      };

      const response = await api.post(
        "activityCalendarService/createEvent",
        payload
      );

      if (response.data?.data?.[0]?.status) {
        message.success("Holiday created successfully");
        setIsCreateModalVisible(false);
        createForm.resetFields();
        fetchHolidays(selectedCompany.companyId, selectedCompany.companyType);
      } else {
        message.error(
          response.data?.data?.[0]?.msg || "Failed to create holiday"
        );
      }
    } catch (error) {
      console.error("Error creating holiday:", error);
      message.error("Failed to create holiday");
    }
  };

  const handleEditHoliday = async (values: any) => {
    if (!editingHoliday) return;

    try {
      const payload = {
        id: editingHoliday.id,
        title: values.title,
        description: values.description,
        start_date: dayjs(values.date).format("YYYY-MM-DD"),
        end_date: dayjs(values.date).format("YYYY-MM-DD"),
        is_all_day: true,
        event_type: "2",
        is_public: true,
        signature: getSignature(),
      };
      const response = await api.post(
        "activityCalendarService/updateEvent",
        payload
      );

      if (response.data?.message[0]?.status) {
        message.success(
          response.data?.message?.[0]?.msg || "Holiday updated successfully"
        );

        setIsEditModalVisible(false);
        setEditingHoliday(null);
        editForm.resetFields();

        fetchHolidays(selectedCompany.companyId, selectedCompany.companyType);
      } else {
        message.error(
          response.data?.message?.[0]?.msg || "Failed to update holiday"
        );
      }
    } catch (error) {
      console.error("Error updating holiday:", error);
      message.error("Failed to update holiday");
    }
  };

  const handleDeleteHoliday = async (holiday: Holiday) => {
    try {
      const payload = {
        id: holiday.id,
        signature: getSignature(),
      };

      const response = await api.post(
        "activityCalendarService/deleteEvent",
        payload
      );

      if (response.data?.message[0]?.status) {
        message.success("Holiday deleted successfully");
        fetchHolidays(selectedCompany.companyId, selectedCompany.companyType);
      } else {
        message.error(
          response.data?.data?.[0]?.msg || "Failed to delete holiday"
        );
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
      message.error("Failed to delete holiday");
    }
  };

  const openEditModal = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    editForm.setFieldsValue({
      title: holiday.title,
      description: holiday.description,
      date: dayjs(holiday.start_date),
      type: holiday.is_public ? "Public" : "Private",
    });
    setIsEditModalVisible(true);
  };

  // const handleDownloadTemplate = () => {
  //   const templateData = [
  //     {
  //       title: "Labour Day",
  //       description: "International Workers Day",
  //       date: "2024-05-01",
  //       type: "Public",
  //     },
  //   ];

  //   const ws = XLSX.utils.json_to_sheet(templateData);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Holidays Template");

  //   const fileName = `holiday_template_${dayjs().format("YYYY_MM_DD")}.xlsx`;
  //   XLSX.writeFile(wb, fileName);
  //   message.success("Template downloaded successfully");
  // };

  const handleDownloadTemplate = () => {
    // Create template data matching the CSV upload requirements
    const templateData = [

      {
        title: "Labour Day",
        description: "International Workers Day",
        date: "2024-05-01",
        type: "Public",
      },
    ];

    // Create CSV content
    const headers = ["title", "description", "date", "type"];
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(','));

    // Add data rows
    templateData.forEach(row => {
      const values = [
        `"${row.title}"`,
        `"${row.description}"`,
        `"${row.date}"`,
        `"${row.type}"`
      ];
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `holiday_template_${dayjs().format("YYYY_MM_DD")}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("CSV template downloaded successfully");
  };

  const handleExportHolidays = () => {
    if (!holidays.length) {
      message.warning("No holidays to export");
      return;
    }

    const exportData = holidays.map((holiday) => ({
      Title: holiday.title,
      Date: holiday.start_date,
      Description: holiday.description || "",
      Type: holiday.is_public ? "Public" : "Private",
      "Created Date": holiday.createdAt
        ? dayjs(holiday.createdAt).format("DD-MM-YYYY")
        : "-",
    }));

    switch (selectedFormat) {
      case "csv":
        exportAsCSV(exportData);
        break;
      case "excel":
        exportAsExcel(exportData);
        break;
      case "pdf":
        exportAsPDF(exportData);
        break;
    }

    setIsDownloadModalVisible(false);
  };

  const exportAsCSV = (data: any[]) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holidays");

    const fileName = `holidays_${selectedCompany.companyName}_${dayjs().format(
      "YYYY_MM_DD_HH_mm_ss"
    )}.csv`;
    XLSX.writeFile(wb, fileName);
    message.success("CSV exported successfully");
  };

  const exportAsExcel = (data: any[]) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Holidays");

    const fileName = `holidays_${selectedCompany.companyName}_${dayjs().format(
      "YYYY_MM_DD_HH_mm_ss"
    )}.xlsx`;
    XLSX.writeFile(wb, fileName);
    message.success("Excel exported successfully");
  };

  const exportAsPDF = (data: any[]) => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text("Holidays Report", 14, 15);
    doc.setFontSize(10);
    doc.text(
      `Company: ${selectedCompany.companyName} (${selectedCompany.companyType})`,
      14,
      25
    );
    doc.text(`Total Holidays: ${holidays.length}`, 14, 32);
    doc.text(`Generated: ${dayjs().format("DD/MM/YYYY HH:mm")}`, 14, 39);

    // Add table
    const columns = ["Title", "Date", "Type", "Description"];
    const tableData = data.map((item) => [
      item.Title,
      item.Date,
      item.Type,
      item.Description || "-",
    ]);

    doc.autoTable({
      head: [columns],
      body: tableData,
      startY: 45,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20 },
        3: { cellWidth: 60 },
      },
    });

    const fileName = `holidays_${selectedCompany.companyName}_${dayjs().format(
      "YYYY_MM_DD_HH_mm_ss"
    )}.pdf`;
    doc.save(fileName);
    message.success("PDF exported successfully");
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "start_date",
      key: "date",
      render: (date: string) => (
        <div className="d-flex align-items-center">
          <CalendarOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          <span>{dayjs(date).format("DD MMM YYYY")}</span>
        </div>
      ),
      sorter: (a: Holiday, b: Holiday) =>
        dayjs(a.start_date).unix() - dayjs(b.start_date).unix(),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a: Holiday, b: Holiday) => a.title.localeCompare(b.title),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) => description || "-",
    },
    {
      title: "Type",
      dataIndex: "is_public",
      key: "type",
      render: (is_public: boolean) => (
        <Tag color={is_public ? "blue" : "purple"}>
          {is_public ? "Public" : "Private"}
        </Tag>
      ),
      sorter: (a: Holiday, b: Holiday) =>
        (a.is_public ? 1 : 0) - (b.is_public ? 1 : 0),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: Holiday) => {
        const isUpcoming = dayjs(record.start_date).isAfter(dayjs(), "day");
        const isToday = dayjs(record.start_date).isSame(dayjs(), "day");

        if (isToday) {
          return <Tag color="green">Today</Tag>;
        } else if (isUpcoming) {
          return <Tag color="orange">Upcoming</Tag>;
        } else {
          return <Tag color="default">Past</Tag>;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Holiday) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              Modal.info({
                title: "Holiday Details",
                content: (
                  <div>
                    <p>
                      <strong>Title:</strong> {record.title}
                    </p>
                    <p>
                      <strong>Date:</strong>{" "}
                      {dayjs(record.start_date).format("DD MMM YYYY")}
                    </p>
                    <p>
                      <strong>Type:</strong>{" "}
                      {record.is_public ? "Public" : "Private"}
                    </p>
                    {record.description && (
                      <p>
                        <strong>Description:</strong> {record.description}
                      </p>
                    )}
                    {record.created_by?.name && (
                      <p>
                        <strong>Created by:</strong> {record.created_by.name}
                      </p>
                    )}
                    {record.createdAt && (
                      <p>
                        <strong>Created:</strong>{" "}
                        {dayjs(record.createdAt).format("DD MMM YYYY")}
                      </p>
                    )}
                  </div>
                ),
              });
            }}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            disabled={record.is_generated} // Disable edit for generated events
          />
          <Popconfirm
            title="Are you sure you want to delete this holiday?"
            onConfirm={() => handleDeleteHoliday(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={record.is_generated} // Disable delete for generated events
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div
      className="page-wrapper"
      style={{
        paddingTop: "64px",
        minHeight: "100vh",
        backgroundColor: "#f0f2f5",
      }}
    >
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Holiday Management</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Calendar</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Holiday Upload
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="me-3">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateModalVisible(true)}
                disabled={!selectedCompany.companyId}
              >
                Create Holiday
              </Button>
            </div>
            <div className="me-3">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </div>

            <div className="me-3">
              <Upload
                accept=".csv,.xlsx,.xls"
                beforeUpload={handleBeforeUpload}
                showUploadList={false}
                disabled={uploadLoading || !selectedCompany.companyId}
              >
                <Button icon={<UploadOutlined />} loading={uploadLoading}>
                  Upload File
                </Button>
              </Upload>
            </div>

            <div className="me-3">
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={() => setIsDownloadModalVisible(true)}
                disabled={holidays.length === 0}
              >
                Export Holidays
              </Button>
            </div>

            <div className="me-3">
              <CommonSelect
                options={companyList.map((company: any) => ({
                  value: `${company.id}-${company.type}`, // 🔑 unique key
                  label: `${company.name} - ${company.type}`,
                }))}
                defaultValue={
                  selectedCompany.companyId && selectedCompany.companyType
                    ? {
                      value: `${selectedCompany.companyId}-${selectedCompany.companyType}`,
                      label: `${selectedCompany.companyName} - ${selectedCompany.companyType}`,
                    }
                    : undefined
                }
                onChange={(option: any) => {
                  const [companyId, companyType] = option.value.split("-");
                  handleCompanyChange(Number(companyId), companyType);
                }}
                placeHolder="Select Company"
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row">
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-primary flex-shrink-0">
                    <i className="ti ti-calendar fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Total Holidays
                    </p>
                    <h4>{stats.total}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-success flex-shrink-0">
                    <i className="ti ti-building fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Public Holidays
                    </p>
                    <h4>{stats.public}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center overflow-hidden">
                  <span className="avatar avatar-lg bg-info flex-shrink-0">
                    <i className="ti ti-calendar-time fs-16" />
                  </span>
                  <div className="ms-2 overflow-hidden">
                    <p className="fs-12 fw-medium mb-1 text-truncate">
                      Upcoming Holidays
                    </p>
                    <h4>{stats.upcoming}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Data Card */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="d-flex align-items-center"></div>
          </div>
          <div className="card-body p-0">
            {holidays.length > 0 ? (
              <Table
                className="table datanew dataTable no-footer"
                columns={columns}
                dataSource={holidays}
                rowKey={(record) =>
                  record.id?.toString() ||
                  `${record.start_date}-${record.title}`
                }
                loading={loading}
                pagination={{
                  locale: { items_per_page: "" },
                  nextIcon: <i className="ti ti-chevron-right" />,
                  prevIcon: <i className="ti ti-chevron-left" />,
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30"],
                }}
              />
            ) : (
              <div className="text-center p-5">
                <CalendarOutlined
                  style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }}
                />
                <h4>No holidays found</h4>
                <p className="text-muted">
                  {selectedCompany.companyId
                    ? "Upload a holiday CSV file or create a new holiday for this company"
                    : "Please select a company first"}
                </p>
                {selectedCompany.companyId && (
                  <Space>
                    <Upload
                      accept=".csv,.xlsx,.xls"
                      beforeUpload={handleBeforeUpload}
                      showUploadList={false}
                      disabled={uploadLoading}
                    >
                      <Button type="primary" icon={<UploadOutlined />}>
                        Upload CSV File
                      </Button>
                    </Upload>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsCreateModalVisible(true)}
                    >
                      Create Holiday
                    </Button>
                  </Space>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Holiday Modal */}
      <Modal
        title="Create New Holiday"
        visible={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateHoliday}
        >
          <Form.Item
            name="title"
            label="Holiday Title"
            rules={[{ required: true, message: "Please enter holiday title" }]}
          >
            <Input placeholder="Enter holiday title" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              disabledDate={(current) =>
                current && current < dayjs().startOf("day")
              }
            />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Enter holiday description (optional)"
            />
          </Form.Item>

          <Form.Item name="type" label="Type" initialValue="Public">
            <Select disabled>
              <Option value="Public">Public Holiday</Option>
              {/* <Option value="Private">Private Holiday</Option> */}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setIsCreateModalVisible(false);
                  createForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Holiday
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Holiday Modal */}
      <Modal
        title="Edit Holiday"
        visible={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingHoliday(null);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditHoliday}>
          <Form.Item
            name="title"
            label="Holiday Title"
            rules={[{ required: true, message: "Please enter holiday title" }]}
          >
            <Input placeholder="Enter holiday title" />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea
              rows={3}
              placeholder="Enter holiday description (optional)"
            />
          </Form.Item>

          {/* <Form.Item name="type" label="Type">
            <Select>
              <Option value="Public">Public Holiday</Option>
              <Option value="Private">Private Holiday</Option>
            </Select>
          </Form.Item> */}

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setIsEditModalVisible(false);
                  setEditingHoliday(null);
                  editForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Update Holiday
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Export Modal */}
      <Modal
        title="Export Holidays"
        visible={isDownloadModalVisible}
        onCancel={() => {
          setIsDownloadModalVisible(false);
          setSelectedFormat("csv");
        }}
        footer={null}
        width={500}
      >
        <div className="form-group" style={{ marginTop: 16 }}>
          <label>
            <strong>Export Format</strong>
          </label>
          <div style={{ marginBottom: 16 }}>
            <p>Select the format to export holiday data:</p>
          </div>

          <div className="d-flex justify-content-between mb-3">
            <Button
              type={selectedFormat === "csv" ? "primary" : "default"}
              icon={<FileTextOutlined />}
              onClick={() => setSelectedFormat("csv")}
              style={{ width: "32%" }}
            >
              CSV
            </Button>
            <Button
              type={selectedFormat === "excel" ? "primary" : "default"}
              icon={<FileExcelOutlined />}
              onClick={() => setSelectedFormat("excel")}
              style={{ width: "32%" }}
            >
              Excel
            </Button>
            <Button
              type={selectedFormat === "pdf" ? "primary" : "default"}
              icon={<FilePdfOutlined />}
              onClick={() => setSelectedFormat("pdf")}
              style={{ width: "32%" }}
            >
              PDF
            </Button>
          </div>
        </div>

        <div className="form-group" style={{ marginTop: 16 }}>
          <label>
            <strong>Company Information</strong>
          </label>
          <div
            style={{
              padding: "10px",
              background: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <p>
              <strong>Company:</strong> {selectedCompany.companyName}
            </p>
            <p>
              <strong>Type:</strong> {selectedCompany.companyType}
            </p>
            <p>
              <strong>Total Holidays:</strong> {holidays.length}
            </p>
          </div>
        </div>

        {holidays.length === 0 && (
          <Alert
            message="No Data"
            description="There are no holidays to export for this company."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 20,
          }}
        >
          <Button
            onClick={() => {
              setIsDownloadModalVisible(false);
              setSelectedFormat("csv");
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleExportHolidays}
            disabled={holidays.length === 0}
            icon={
              selectedFormat === "csv" ? (
                <FileTextOutlined />
              ) : selectedFormat === "excel" ? (
                <FileExcelOutlined />
              ) : (
                <FilePdfOutlined />
              )
            }
          >
            Export {selectedFormat.toUpperCase()}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default HolidayUpload;
